import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
    BlobType,
    FileModel,
    ListSearch,
    ModuleV1Read,
    UrlChange,
    UserInfo,
    ValidAndInvalidFiles,
    WorkflowStarterInputChangeEvent,
    WorkflowStarterType,
} from '@ca/core/models';
import { FilePreviewService, FilesValidatorService, UserNotificationService, UserService } from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import clone from 'clone-deep';
import { Guid } from 'guid-typescript';
import { take, tap } from 'rxjs';
import { CreateFolder } from 'src/app/core/models/create-folder.model';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import * as FileUtils from '../../../../utils/file/file-utils';
import { isApeerFile, isWebFile } from '../../../../utils/workflow/workflow-runner-utils';
import { AddOrEditUrlModalComponent } from '../add-or-edit-url-modal/add-or-edit-url-modal.component';
import { FileExplorerTabs } from '../file-explorer/file-explorer.component';
import { FileSelectionComponent } from '../file-selection/file-selection.component';

@UntilDestroy()
@Component({
    selector: 'ca-workflow-starter',
    templateUrl: './workflow-starter.component.html',
    styleUrls: ['./workflow-starter.component.scss'],
})
export class WorkflowStarterComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() uploaderKey: string;
    @Input() isDisabled: boolean;

    /**
     * URLs of the files previously saved in workflow. Don't use this for logic. First get file models and use them.
     */
    @Input() readonly input: Array<string> = [];
    @Input() isWorkflowRunning = false;
    @Input() isRunCompleted = false;
    @Input() isRunFailed = false;
    @Input() isRunPending = false;
    @Input() allowEmptyFileList = true;
    @Input() allowMultipleFiles = true;
    @Input() isInRunner = true;
    @Input() module?: ModuleV1Read;
    @Input() isRerun = false;
    @Input() flowId: string;
    @Input() isPublicWorkflow: boolean;
    @Input() initiallySelectedFiles: Array<string> = [];

    // If provided files will be uploaded to provided folder
    @Input() targetFolderId?: string;
    // If provided and targetFolderId is not provided, folder in provided
    // path will be created and files will be uploaded to it
    @Input() createTargetFolder?: CreateFolder;

    // If provided, will upload the file and connect it to given dataset
    @Input() datasetId?: string;

    // Config Options to hide file APEER or URL upload
    @Input() hideUrlUpload = false;
    @Input() hideApeerFileSelection = false;
    @Input() showViewAllButton: boolean = true;
    @Input() tabsToHide: FileExplorerTabs[] = [];

    @Input() maxFileSizeInBytes: number = environment.defaultMaxFileSizeInBytes;
    @Input() maxFileSizeCustomMessage: string | undefined;
    @Input() supportedFileTypes: string[] | undefined;
    @Input() notSupportedFileTypeCustomMessage: string | undefined;
    @Input() cziConversion: boolean = false;
    @Input() allowFileWithSameNameAndDifferentExtensions: boolean = true;

    @Output() onInputChange = new EventEmitter<WorkflowStarterInputChangeEvent>();
    @Output() onWorkflowStarterTypeChange = new EventEmitter<WorkflowStarterType>();

    @ViewChild('fileSelectionRef', { static: false }) fileSelectionRef: FileSelectionComponent;

    // change to UserFile for abstract files usage
    userFileBlobType: BlobType = BlobType.UserFile;
    WorkflowStarterType = WorkflowStarterType;

    // files currently being managed, e.g. inputs for workflow
    selectedFiles: FileModel[] = [];

    showArivisPreviewer = false;
    selectedPreviewFile?: FileModel;
    fileUploadHeightClass = '';

    /**
     * The "screen" where the starter should be initialized with.
     * "Upload" contains the uploader, as well as the options "APEER Files", "Web", on the left, and the selected files
     * on the right, so we always start with that.
     */
    workflowStarterType = WorkflowStarterType.SelectionMethods;

    /**
     * This ListSearch object will be passed into BOTH FileSelectionComponent instances.
     * They will be kept in sync in order to keep searching and sorting in sync between the components
     */
    searchAndSortOptionsForFileSelection: ListSearch = {
        pageNumber: 1,
        pageSize: 10,
        searchQuery: '',
        sortBy: '',
    };

    private runningModeSet = false;

    private operationId: string;
    private apeerFilesMap = new Map<string, FileModel>();

    constructor(
        private filePreviewService: FilePreviewService,
        private arivisService: ArivisService,
        private modal: NgbModal,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private userService: UserService,
        private cziConverterService: CziConverterService,
        private filesValidatorService: FilesValidatorService,
    ) {}

    ngAfterViewInit(): void {
        this.setFileUploadHeightClasses();
    }

    ngOnInit(): void {
        // Input URLs come form another property (this.input). This is because we want them to be passed just once.
        // If we would directly bind to this property, it would constantly trigger changing the input parameter, because
        // f. ex. in runner.component we pass those input parameters by a method (which gets triggered in every cd).
        // By this line we make sure the URLs just get passed once, and not in every cd.
        const inputUrls = this.input.filter(f => f !== '');

        this.selectedFiles = this.getSelectedFilesAccordingToWorkflowStarter(inputUrls);

        this.checkAndSetRunningMode();

        if (!this.isWorkflowRunning && !this.isRerun && this.selectedFiles.length > 0 && this.selectedFiles.some(url => FileModel.isApeerFile(url.token))) {
            this.toggleArivisPreviewer();
        }

        this.onWorkflowStarterTypeChange.emit(this.workflowStarterType);
        this.operationId = Guid.create().toString();
    }

    ngOnChanges(): void {
        this.checkAndSetRunningMode();
    }

    ngOnDestroy(): void {
        this.cziConverterService.clearConversionMap(this.operationId);
    }

    get userInfo(): UserInfo | undefined {
        return this.userService.myInfo;
    }

    get isLastIndex(): boolean {
        const pagedSelectedFiles: FileModel[] =
            this.fileSelectionRef != null && this.fileSelectionRef.pagedSelectedFiles != null ? this.fileSelectionRef.pagedSelectedFiles : [];
        return (
            pagedSelectedFiles.length > 0 &&
            pagedSelectedFiles.findIndex(file => this.selectedPreviewFile && this.selectedPreviewFile.token === file.token) === pagedSelectedFiles.length - 1
        );
    }

    get isFirstIndex(): boolean {
        const pagedSelectedFiles: FileModel[] =
            this.fileSelectionRef != null && this.fileSelectionRef.pagedSelectedFiles != null ? this.fileSelectionRef.pagedSelectedFiles : [];
        return (
            pagedSelectedFiles.length > 0 &&
            pagedSelectedFiles.findIndex(file => this.selectedPreviewFile && this.selectedPreviewFile.token === file.token) === 0
        );
    }

    get fileExplorerStartingTab(): FileExplorerTabs {
        return FileExplorerTabs.MY_FILES;
    }

    setFileUploadHeightClasses(): void {
        // wait for buttons visibility
        setTimeout(() => {
            const length = document.querySelectorAll('.selection-method-btn').length ?? 0;
            this.fileUploadHeightClass = `file-upload-buttons-length-${length}`;
        }, 0);
    }

    updateSearchAndSortOptions(searchParams: ListSearch): void {
        this.searchAndSortOptionsForFileSelection = { ...searchParams };
        this.triggerSelectedFilesUpdate();
    }

    setWorkflowStarterType(type: WorkflowStarterType): void {
        this.workflowStarterType = type;
        this.onWorkflowStarterTypeChange.emit(this.workflowStarterType);
    }

    toggleArivisPreviewer(value?: boolean): void {
        this.showArivisPreviewer = value != null ? value : !this.showArivisPreviewer;
    }

    removeUpload(file: FileModel): void {
        const files = this.selectedFiles.filter((fileEntry: FileModel) => fileEntry !== file);
        this.cziConverterService.removeFileFromConversionMap(this.operationId, file.id);
        this.updateSelectedFiles(files);

        if (this.selectedFiles.length === 0) {
            this.showArivisPreviewer = false;
        }
    }

    uploadStarted(): void {
        this.setWorkflowStarterType(WorkflowStarterType.SelectionMethods);
    }

    /**
     * The FileReference component (which handles adding web URLs) always emits ALL URLs at once.
     * So on each emit we have to remove all web URLs and re-add them
     */
    handleAddWebUrls(files: FileModel[]): void {
        // remove all web URLs
        const filesWithoutWebUrls: FileModel[] = this.selectedFiles.filter((f: FileModel) => f.storageType !== 'Web');

        this.selectedFiles = [...filesWithoutWebUrls];

        this.addToSelectedFiles(files);
    }

    handleSelectFile(file: FileModel): void {
        this.filesValidatorService
            .getValidAndInvalidFiles$<FileModel>([file], [], [], {
                errorNotificationMode: 'notification',
                isFileTypeSupportedOptions: { supportedFileTypes: this.supportedFileTypes, errorMessageKey: this.notSupportedFileTypeCustomMessage },
                isMaxFileSizeExceededOptions: { maxInputSizeInBytes: this.maxFileSizeInBytes },
                checkFileNameOptions: { checkSingleFileNameForInvalidCharacters: true },
            })
            .pipe(
                tap((validAndInvalidFiles: ValidAndInvalidFiles<FileModel>) => {
                    if (validAndInvalidFiles.validFiles.length > 0) {
                        this.addToSelectedFiles(validAndInvalidFiles.validFiles, true, true);
                    }
                }),
                take(1),
            )
            .subscribe();
    }

    resetWorkflowStarter(): void {
        this.workflowStarterType = WorkflowStarterType.SelectionMethods;
        this.selectedFiles.forEach(f => this.cziConverterService.removeFileFromConversionMap(this.operationId, f.id));
        this.updateSelectedFiles([]);

        this.showArivisPreviewer = false;
    }

    handleSelectedFileChange(selectedFile: FileModel | undefined): void {
        this.selectedPreviewFile = selectedFile;
        if (
            this.selectedPreviewFile &&
            this.selectedPreviewFile.token != null &&
            !this.isSelectedFileSupportedByArivis() &&
            this.arivisService.isSupportedByFilePreviewer(this.selectedPreviewFile.token)
        ) {
            this.filePreviewService.openWithURL(this.selectedPreviewFile.token, this.selectedPreviewFile.type, 'inline');
        }
    }

    isSelectedFileSupportedByArivis(): boolean {
        return this.arivisService.isFilePreviewSupportedByArivis(this.selectedPreviewFile);
    }

    isSelectedFileSupportedByFilePreviewer(): boolean {
        if (this.selectedPreviewFile == null) {
            return false;
        }
        return this.arivisService.isSupportedByFilePreviewer(this.selectedPreviewFile.token || '');
    }

    handlePreviousNextFileClick(type: 'previous' | 'next'): void {
        if (!this.fileSelectionRef) {
            return;
        }
        const pagedSelectedFiles: FileModel[] = this.fileSelectionRef.pagedSelectedFiles;
        const selectedIndex: number =
            pagedSelectedFiles.length > 0
                ? pagedSelectedFiles.findIndex(file => this.selectedPreviewFile && this.selectedPreviewFile.token === file.token)
                : -1;
        if (type === 'previous' && selectedIndex > 0) {
            this.fileSelectionRef.handleFileClick(selectedIndex - 1);
        }
        if (type === 'next' && selectedIndex < pagedSelectedFiles.length - 1) {
            this.fileSelectionRef.handleFileClick(selectedIndex + 1);
        }
    }

    removeAllUploads(): void {
        this.selectedFiles.forEach((file: FileModel) => this.removeUpload(file));

        this.showArivisPreviewer = false;
    }

    addUrl(): void {
        const modalRef = this.modal.open(AddOrEditUrlModalComponent);
        modalRef.componentInstance.selectedFiles = this.selectedFiles;
        if (this.maxFileSizeInBytes < environment.defaultMaxFileSizeInBytes) {
            modalRef.componentInstance.maxFileSizeInBytes = this.maxFileSizeInBytes;
            modalRef.componentInstance.maxFileSizeCustomMessage = this.maxFileSizeCustomMessage;
        }

        if (this.supportedFileTypes != null && this.supportedFileTypes.length > 0) {
            modalRef.componentInstance.supportedFileTypes = this.supportedFileTypes;
            modalRef.componentInstance.notSupportedFileTypeCustomMessage = this.notSupportedFileTypeCustomMessage;
        }

        modalRef.result
            .then((urlChange: UrlChange) => {
                this.addToSelectedFiles([FileModel.fromWebUrl(urlChange.newUrl)]);
                this.workflowStarterType = WorkflowStarterType.SelectionMethods;
            })
            .catch(() => {
                // do nothing
            });
    }

    updateFileUrl(url: string): void {
        const modalRef = this.modal.open(AddOrEditUrlModalComponent);
        const addOrEditUrlModalComponent = <AddOrEditUrlModalComponent>modalRef.componentInstance;
        addOrEditUrlModalComponent.url = url;
        addOrEditUrlModalComponent.urlMode = 'EDIT';
        addOrEditUrlModalComponent.selectedFiles = this.selectedFiles;

        modalRef.result
            .then((urlChange: UrlChange) => {
                const selectedFiles = [...this.selectedFiles];
                for (let index = 0; index < selectedFiles.length; index++) {
                    if (selectedFiles[index].url === urlChange.previousUrl) {
                        selectedFiles[index] = FileModel.fromWebUrl(urlChange.newUrl);
                        break;
                    }
                }
                this.updateSelectedFiles(selectedFiles);
            })
            .catch(() => {
                // do nothing
            });
    }

    addTestFiles(): void {
        this.selectedFiles = [];
        this.addToSelectedFiles(this.getSelectedFilesAccordingToWorkflowStarter(this.initiallySelectedFiles));
    }

    /**
     * Adds files to selected files array.
     *
     * IMPORTANT: Always use this method to ADD files to selectedFiles() -
     * never add files directly to this.selectedFiles (because this method handles all necessary checks).
     */
    addToSelectedFiles(files?: FileModel[], checkFileConversions = false, areApeerFiles = false): void {
        if (files == null) {
            return;
        }

        if (!this.allowMultipleFiles && this.selectedFiles.length + files.length > 1) {
            this.userNotificationService.showWarning('WORKFLOWS.RUNNER.ONLY_ONE_FILE_ALLOWED');
            return;
        }

        files.forEach((file: FileModel) => {
            // when the token is null, we cannot add the file.
            if (file.token == null) {
                console.error('File does not have any token', file.token);
            } else {
                // Check if file is not added yet
                if (
                    (this.allowFileWithSameNameAndDifferentExtensions && !FileUtils.isFileNameWithExtensionContainedInFiles(file.token, this.selectedFiles)) ||
                    (!this.allowFileWithSameNameAndDifferentExtensions && !FileUtils.isFileNameWithoutExtensionContainedInFiles(file.token, this.selectedFiles))
                ) {
                    const clonedFile: FileModel = clone(file, true);
                    this.selectedFiles = [clonedFile, ...this.selectedFiles];
                    if (this.cziConversion && checkFileConversions) {
                        if (areApeerFiles) {
                            this.apeerFilesMap.set(file.id, file);
                        }
                        this.cziConverterService
                            .convertFileToCziIfNotCziAndStartPolling$(this.operationId, this.selectedFiles, clonedFile, undefined, this.apeerFilesMap)
                            .pipe(untilDestroyed(this))
                            .subscribe();
                    }
                } else {
                    this.userNotificationService.showWarning(
                        this.translateService.instant('VALIDATION.MULTIPLE_FILES_WITH_THE_SAME_NAME_SELECTED', { name: file.name }),
                    );
                }
            }
        });

        this.triggerSelectedFilesUpdate();
    }

    get isCziConversionInProgress(): boolean {
        if (!this.cziConversion) {
            return false;
        }
        return this.cziConverterService.areSomeFilesActiveOrUnknown(this.selectedFiles);
    }

    get numberOfConversionFailedFiles(): number {
        return this.cziConverterService.getNumberOfConversionFailedFiles(this.selectedFiles);
    }

    private getSelectedFilesAccordingToWorkflowStarter(inputUrls: string[]): FileModel[] {
        // Set inputs initially in order to keep URLs when workflow run has been started.
        return inputUrls.reduce((selectedFiles: FileModel[], url: string) => {
            if (isApeerFile(url) || isWebFile(url)) {
                selectedFiles.push(FileModel.fromWebUrl(url));
            }
            return selectedFiles;
        }, []);
    }

    private triggerSelectedFilesUpdate(): void {
        this.onInputChange.emit({
            inputFiles: this.selectedFiles,
            moduleId: this.module != null ? this.module.id : undefined,
        });
    }

    private updateSelectedFiles(files: FileModel[]): void {
        this.selectedFiles = [...files];
        this.cziConverterService.updateOperationFileStatuses$(this.operationId, this.selectedFiles, this.apeerFilesMap).pipe(untilDestroyed(this)).subscribe();
        this.triggerSelectedFilesUpdate();
    }

    private checkAndSetRunningMode(): void {
        if (!this.runningModeSet && (this.isRerun || this.isWorkflowRunning)) {
            this.runningModeSet = true;
            this.toggleArivisPreviewer(true);
        }
    }
}
